import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, Stack, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MedicationIcon from '@mui/icons-material/Medication';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ScienceIcon from '@mui/icons-material/Science';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TriageBadge } from '@/components/SeverityChip';
import { AISuggestionCard } from '@/components/clinical/AISuggestionCard';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { clinician } from '@/api/endpoints';
import { formatDateTime, formatTime } from '@/utils/format';
import { useIsMobile } from '@/hooks/useIsMobile';
import type {
  AIAssistanceEvent, AppointmentDetail, Encounter, TriageLevel,
} from '@/types/medical';

/**
 * Encounter editor.
 *
 * Flow:
 *   1. Open from /clinician/appointments/:id
 *   2. "Start consult" creates the MedicalEncounter and moves status to in_consult
 *   3. SOAP fields autosave (debounced) via PATCH /encounters/:id
 *   4. AI scribe: clinician dictates → AI proposes a draft → AISuggestionCard
 *      surfaces it; on accept/edit, fields are populated
 *   5. Quick actions: write Rx (sub-page), issue sick note, order labs
 *   6. Lock the note (irreversible) and complete the appointment
 *   7. After lock, addendum dialog is the only way to add content
 */

export function ClinicianEncounterEditorPage() {
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Pending Rx and sick note flows are reached via sub-routes; we just
  // surface buttons here.
  const apptQ = useApiQuery({
    queryKey: ['clinician', 'appointments', id],
    queryFn:  () => clinician.appointments.get(id),
    enabled:  Boolean(id),
  });

  const startMut = useApiMutation({
    mutationFn: () => clinician.appointments.start(id),
    onSuccess: () => {
      toast.success('Encounter started.');
      qc.invalidateQueries({ queryKey: ['clinician', 'appointments', id] });
    },
    onError: (e) => toast.error(e.message),
  });

  // Composing AI suggestion (e.g. clinical scribe) lifts to a panel.
  const [aiEvent, setAiEvent] = useState<AIAssistanceEvent | null>(null);
  const [aiOutput, setAiOutput] = useState<unknown>(null);
  const [aiPrompt, setAiPrompt] = useState('');

  const aiSuggestMut = useApiMutation({
    mutationFn: (prompt: string) => clinician.ai.suggest(
      'scribe_v1',
      { dictation: prompt, appointment_id: id },
      { appointment_id: id, patient_id: apptQ.data?.patient as unknown as number },
    ),
    onSuccess: (event) => {
      setAiEvent(event);
      // The output isn't persisted — we keep it locally so the clinician
      // can review before deciding.
      const o = (event as AIAssistanceEvent & { _output?: unknown })._output;
      setAiOutput(o ?? event.output_summary);
    },
    onError: (e) => toast.error(e.message),
  });

  const aiDecideMut = useApiMutation({
    mutationFn: (vars: { eventId: string; action: 'accepted' | 'edited' | 'rejected' | 'escalated'; reason?: string }) =>
      clinician.ai.decide(vars.eventId, vars.action, undefined, vars.reason),
    onSuccess: (event) => {
      setAiEvent(event);
      toast.success(`AI suggestion ${event.human_action}.`);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Encounter"
        breadcrumbs={[
          { label: 'Appointments', to: '/clinician/appointments' },
          { label: 'Detail' },
        ]}
      />

      <DataState query={apptQ}>
        {(appt) => (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <PatientHeaderCard appt={appt} />

              {appt.status === 'in_consult' || appt.status === 'completed' ? (
                <EncounterPanel
                  appointmentId={id}
                  encounterId={appt.encounter_id ?? null}
                  locked={appt.status === 'completed'}
                />
              ) : (
                <StartConsultCard
                  status={appt.status}
                  onStart={() => startMut.mutate()}
                  busy={startMut.isPending}
                />
              )}

              {/* AI scribe panel */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <SmartToyIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      AI clinical scribe
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary"
                              sx={{ display: 'block', mb: 1.5 }}>
                    Paste your dictation; the scribe drafts a SOAP-style note.
                    The draft is NOT saved until you accept it.
                  </Typography>
                  <TextField
                    fullWidth multiline minRows={3}
                    placeholder="Patient is a 54-year-old male presenting with…"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    disabled={aiSuggestMut.isPending}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      onClick={() => aiSuggestMut.mutate(aiPrompt)}
                      disabled={!aiPrompt.trim() || aiSuggestMut.isPending}
                    >
                      Draft note
                    </Button>
                  </Stack>
                  {aiEvent && (
                    <Box sx={{ mt: 2 }}>
                      <AISuggestionCard
                        event={aiEvent}
                        output={aiOutput}
                        busy={aiDecideMut.isPending}
                        onDecide={(action, reason) =>
                          aiDecideMut.mutateAsync({
                            eventId: aiEvent.id,
                            action: action as 'accepted' | 'edited' | 'rejected' | 'escalated',
                            ...(reason !== undefined && { reason }),
                          })
                        }
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <QuickActionsCard
                appointmentId={id}
                disabled={appt.status !== 'in_consult'}
                onWriteRx={() => nav(`/clinician/prescriptions?appointment=${id}`)}
                onIssueSickNote={() => nav(`/clinician/sick-notes?appointment=${id}`)}
                onOrderLabs={() => nav(`/clinician/lab-orders?appointment=${id}`)}
              />
              <CompleteCard
                appointmentId={id}
                status={appt.status}
                onCompleted={() => nav('/clinician')}
              />
            </Grid>
          </Grid>
        )}
      </DataState>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function PatientHeaderCard({ appt }: { appt: AppointmentDetail }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" color="text.secondary">
              Patient {appt.patient_initials}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ mb: 0.5 }}>
              {appt.procedure?.name ?? 'Appointment'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <span className="tabular">
                {formatTime(appt.start_at)} – {formatTime(appt.end_at)}
              </span>
              {' · '}
              {appt.modality.replace(/_/g, ' ')}
            </Typography>
            {appt.chief_complaint && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.subtle', borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  In their words:
                </Typography>{' '}
                <Typography variant="body2" component="span">
                  {appt.chief_complaint}
                </Typography>
              </Box>
            )}
          </Box>
          <Stack alignItems="flex-end" spacing={1}>
            <Chip
              label={appt.status.replace(/_/g, ' ')}
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
            {appt.ai_triage_level && (
              <TriageBadge level={appt.ai_triage_level as TriageLevel} small />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function StartConsultCard({
  status, onStart, busy,
}: {
  status: AppointmentDetail['status'];
  onStart: () => void;
  busy: boolean;
}) {
  const startable = status === 'booked' || status === 'confirmed' || status === 'checked_in';
  return (
    <Card>
      <CardContent>
        <Stack alignItems="flex-start" spacing={2}>
          <Typography variant="body1">
            {startable
              ? 'When you start, the encounter row is created and the appointment moves to "in consult". You can write the SOAP note from there.'
              : `Cannot start consult from status "${status.replace(/_/g, ' ')}".`}
          </Typography>
          <Button
            variant="contained" startIcon={<PlayArrowIcon />}
            onClick={onStart} disabled={!startable || busy}
          >
            Start consult
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SOAP note panel — autosave on blur, lock action, addendum after lock
// ─────────────────────────────────────────────────────────────────────────────

function EncounterPanel({
  appointmentId, encounterId, locked,
}: {
  appointmentId: string;
  encounterId: number | null;
  locked: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  // Defensive: if encounterId not on appointment, we can fall back to the
  // appointment endpoint that auto-creates one when started. For Tour 3
  // we assume `encounter_id` is present once status='in_consult'.
  const encQ = useApiQuery({
    queryKey: ['clinician', 'encounters', encounterId ?? 0],
    queryFn:  () => clinician.encounters.get(encounterId as number),
    enabled:  encounterId != null,
  });

  return (
    <DataState query={encQ}>
      {(enc) => (
        <SoapNoteCard
          encounter={enc}
          locked={locked || enc.locked}
          onSaved={() => qc.invalidateQueries({
            queryKey: ['clinician', 'encounters', enc.id],
          })}
          onLockedToast={() => toast.success('Encounter locked.')}
          appointmentId={appointmentId}
        />
      )}
    </DataState>
  );
}

function SoapNoteCard({
  encounter, locked, onSaved, onLockedToast,
}: {
  encounter: Encounter;
  locked: boolean;
  onSaved: () => void;
  onLockedToast: () => void;
  appointmentId: string;
}) {
  const [subjective, setSubjective] = useState(encounter.subjective || '');
  const [objective, setObjective]   = useState(encounter.objective || '');
  const [assessment, setAssessment] = useState(encounter.assessment || '');
  const [plan, setPlan]             = useState(encounter.plan || '');
  const [lockOpen, setLockOpen] = useState(false);
  const [addendumOpen, setAddendumOpen] = useState(false);

  const toast = useToast();

  // Reset form when encounter changes (avoids stale values after refetch)
  useEffect(() => {
    setSubjective(encounter.subjective || '');
    setObjective(encounter.objective || '');
    setAssessment(encounter.assessment || '');
    setPlan(encounter.plan || '');
  }, [encounter.id]);

  const updateMut = useApiMutation({
    mutationFn: (patch: Partial<Encounter>) =>
      clinician.encounters.update(encounter.id, patch),
    onSuccess: () => onSaved(),
    onError: (e) => toast.error(e.message),
  });
  const lockMut = useApiMutation({
    mutationFn: () => clinician.encounters.lock(encounter.id),
    onSuccess: () => {
      onLockedToast();
      onSaved();
      setLockOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const addendumMut = useApiMutation({
    mutationFn: (text: string) => clinician.encounters.addAddendum(encounter.id, text),
    onSuccess: () => {
      toast.success('Addendum added.');
      onSaved();
      setAddendumOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const saveOnBlur = (field: keyof Encounter, value: string) => {
    if (locked) return;
    if (encounter[field] === value) return;
    updateMut.mutate({ [field]: value } as Partial<Encounter>);
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <HistoryEduIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                SOAP note
              </Typography>
              {locked && (
                <Chip
                  size="small" icon={<LockIcon fontSize="small" />}
                  label={`locked ${encounter.locked_at ? formatDateTime(encounter.locked_at) : ''}`}
                  variant="outlined"
                />
              )}
            </Stack>
            {!locked && (
              <Button
                variant="outlined" color="error" startIcon={<LockIcon />}
                onClick={() => setLockOpen(true)}
              >
                Lock
              </Button>
            )}
          </Stack>

          <Stack spacing={2.5}>
            <SoapField
              label="Subjective" helper="Patient-reported history."
              value={subjective} onChange={setSubjective}
              onBlur={(v) => saveOnBlur('subjective', v)}
              disabled={locked}
            />
            <SoapField
              label="Objective" helper="Findings, vitals, exam."
              value={objective} onChange={setObjective}
              onBlur={(v) => saveOnBlur('objective', v)}
              disabled={locked}
            />
            <SoapField
              label="Assessment" helper="Clinical reasoning, diagnosis."
              value={assessment} onChange={setAssessment}
              onBlur={(v) => saveOnBlur('assessment', v)}
              disabled={locked}
            />
            <SoapField
              label="Plan" helper="Treatment, follow-up, education."
              value={plan} onChange={setPlan}
              onBlur={(v) => saveOnBlur('plan', v)}
              disabled={locked}
            />
          </Stack>

          {(encounter.icd_codes ?? []).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">ICD codes</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {encounter.icd_codes.map((c, i) => (
                  <Chip key={i} size="small" label={`${c.code} ${c.display}`} variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {locked && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Addenda ({encounter.addenda?.length ?? 0})
              </Typography>
              <Button size="small" variant="outlined"
                      onClick={() => setAddendumOpen(true)}>
                Add addendum
              </Button>
            </Stack>
            {(encounter.addenda ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                The encounter is locked. Use addenda to add information without
                changing the original note.
              </Typography>
            ) : (
              <Stack spacing={1.5} divider={<Divider />}>
                {encounter.addenda.map((a) => (
                  <Box key={a.id}>
                    <Typography variant="caption" color="text.secondary">
                      {a.author_name} · {formatDateTime(a.created_at)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {a.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* Lock dialog */}
      <ConfirmDialog
        open={lockOpen}
        title="Lock this encounter?"
        description="Locking is permanent. After locking, you can only add addenda — the original note can never be changed."
        destructive
        confirmLabel="Lock"
        onCancel={() => setLockOpen(false)}
        onConfirm={() => lockMut.mutateAsync()}
      />

      {/* Addendum dialog */}
      <AddendumDialog
        open={addendumOpen}
        onCancel={() => setAddendumOpen(false)}
        onSubmit={(text) => addendumMut.mutateAsync(text)}
        busy={addendumMut.isPending}
      />
    </>
  );
}

function SoapField({
  label, helper, value, onChange, onBlur, disabled,
}: {
  label: string;
  helper: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="caption" color="text.secondary">{helper}</Typography>
      </Stack>
      <TextField
        fullWidth multiline minRows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onBlur(e.target.value)}
        disabled={disabled}
        sx={{ mt: 0.5 }}
      />
    </Box>
  );
}

function AddendumDialog({
  open, onCancel, onSubmit, busy,
}: {
  open: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => Promise<unknown>;
  busy: boolean;
}) {
  const isMobile = useIsMobile();
  const [text, setText] = useState('');
  return (
    <Dialog fullScreen={isMobile} open={open} onClose={() => !busy && onCancel()} maxWidth="md" fullWidth>
      <DialogTitle>Add addendum</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Addenda are appended to the locked note as separate, signed entries.
          The original SOAP note is never modified.
        </Typography>
        <TextField
          autoFocus fullWidth multiline minRows={4}
          label="Addendum text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          variant="contained" disabled={busy || !text.trim()}
          onClick={async () => { await onSubmit(text.trim()); setText(''); }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function QuickActionsCard({
  disabled, onWriteRx, onIssueSickNote, onOrderLabs,
}: {
  appointmentId: string;
  disabled: boolean;
  onWriteRx: () => void;
  onIssueSickNote: () => void;
  onOrderLabs: () => void;
}) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}>
          Quick actions
        </Typography>
        <Stack spacing={1}>
          <Button variant="contained" fullWidth disabled={disabled}
                  startIcon={<MedicationIcon />} onClick={onWriteRx}>
            Write prescription
          </Button>
          <Button variant="outlined" fullWidth disabled={disabled}
                  startIcon={<LocalHospitalIcon />} onClick={onIssueSickNote}>
            Issue sick note
          </Button>
          <Button variant="outlined" fullWidth disabled={disabled}
                  startIcon={<ScienceIcon />} onClick={onOrderLabs}>
            Order labs
          </Button>
        </Stack>
        {disabled && (
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 1.5 }}>
            Start the consult to enable these actions.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function CompleteCard({
  appointmentId, status, onCompleted,
}: {
  appointmentId: string;
  status: AppointmentDetail['status'];
  onCompleted: () => void;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [followUp, setFollowUp] = useState<string>('');
  const toast = useToast();
  const qc = useQueryClient();

  const completeMut = useApiMutation({
    mutationFn: (days?: number) => clinician.appointments.complete(appointmentId, days),
    onSuccess: () => {
      toast.success('Appointment completed.');
      qc.invalidateQueries({ queryKey: ['clinician'] });
      onCompleted();
    },
    onError: (e) => toast.error(e.message),
  });

  if (status === 'completed') {
    return (
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CheckCircleIcon color="success" />
            <Typography variant="body2">Appointment completed.</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }
  if (status !== 'in_consult') return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}>
          Complete
        </Typography>
        <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mb: 2 }}>
          Completing also locks the encounter (if not already locked).
        </Typography>
        <Button
          variant="contained" fullWidth color="success"
          startIcon={<CheckCircleIcon />} onClick={() => setOpen(true)}
        >
          Complete appointment
        </Button>

        <Dialog fullScreen={isMobile} open={open} onClose={() => !completeMut.isPending && setOpen(false)}
                maxWidth="xs" fullWidth>
          <DialogTitle>Complete appointment</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Optionally suggest a follow-up window. Leave empty if not needed.
            </Typography>
            <TextField
              fullWidth label="Follow-up in days"
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={completeMut.isPending}>Cancel</Button>
            <Button
              variant="contained" color="success"
              disabled={completeMut.isPending}
              onClick={() => completeMut.mutate(
                followUp ? parseInt(followUp, 10) : undefined,
              )}
            >
              Complete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
